import React from "react";
import {observer} from "mobx-react-lite";
import {useSitStores} from "~/stores";
import type {ComplexityTier, ExecutionStatus, Workstream} from "../../../ApiTypes";
import {ComplexityTierValues, ExecutionStatusValues, WorkstreamValues} from "../../../ApiTypes";
import {complexityTierLabel, executionStatusLabel, sitFieldLabel, workstreamLabel} from "../../../sitLabels";
import {SitFarolPill, SitField, SitRead} from "../../../components/design/SitDesignAtoms";
import i18n from "../../../../i18n/i18n";

export const InitiativeGeneralForm: React.FC = observer(() => {
    const {ccStore, initiativeInstanceStore: s} = useSitStores();
    const dis = s.disabled;
    const tier = s.instance.complexity_tier;

    return (
        <>
            <div className="sit-grid">
                <SitField label={sitFieldLabel('title')} req full>
                    <input value={s.instance.title ?? ''} disabled={dis} required
                           onChange={e => s.title = e.target.value}/>
                </SitField>
                {ccStore.showCustomerId &&
                <SitField label={sitFieldLabel('customerId')}>
                    <input value={s.instance.customer_id ?? ''} disabled={dis}
                           onChange={e => s.customerId = e.target.value}/>
                </SitField>}
                <SitField label="Initiative description" full>
                    <textarea rows={2} value={s.description.instance?.description ?? ''} disabled={dis}
                              onChange={e => s.description.description = e.target.value}/>
                </SitField>
                <SitField label="Confidential description" full
                          help="Only visible to the owner, team and Finance.">
                    <textarea rows={2} value={s.description.instance?.confidential_description ?? ''} disabled={dis}
                              onChange={e => s.description.confidential_description = e.target.value}/>
                </SitField>
                <SitField label="Notes" full>
                    <textarea rows={2} value={s.description.instance?.notes ?? ''} disabled={dis}
                              onChange={e => s.description.notes = e.target.value}/>
                </SitField>
                <SitField label={sitFieldLabel('leverGroup')} req>
                    <select value={s.lever_group_id ?? ''} disabled={dis}
                            onChange={e => s.leverGroupId = Number(e.target.value)}>
                        {ccStore.leverGroupRM.result?.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                    </select>
                </SitField>
                <SitField label="Lever" req>
                    <select value={s.instance.lever_id ?? ''} disabled={dis}
                            onChange={e => s.leverId = Number(e.target.value)}>
                        {s.leverChoices.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                    </select>
                </SitField>
                <SitRead label="Lever description" value={s.leverDescription || '—'}/>
                <SitField label={sitFieldLabel('complexityTier')}
                          help="effort/complexity axis — drives the RACI, working group and squad-agenda rules.">
                    <select value={tier ?? ''} disabled={dis}
                            onChange={e => { if (e.target.value) s.complexityTier = Number(e.target.value) as ComplexityTier; }}>
                        <option value="">—</option>
                        {ComplexityTierValues.map(v => <option key={v} value={v}>{complexityTierLabel(v)}</option>)}
                    </select>
                </SitField>
                <SitField label={sitFieldLabel('workstream')}>
                    <select value={s.instance.workstream ?? ''} disabled={dis}
                            onChange={e => { if (e.target.value) s.workstream = Number(e.target.value) as Workstream; }}>
                        <option value="">—</option>
                        {WorkstreamValues.map(v => <option key={v} value={v}>{workstreamLabel(v)}</option>)}
                    </select>
                </SitField>
                <SitField label={sitFieldLabel('executionStatus')}>
                    <select value={s.instance.execution_status ?? ''} disabled={dis}
                            onChange={e => { if (e.target.value) s.executionStatus = Number(e.target.value) as ExecutionStatus; }}>
                        <option value="">—</option>
                        {ExecutionStatusValues.map(v => <option key={v} value={v}>{executionStatusLabel(v)}</option>)}
                    </select>
                </SitField>
            </div>

            {tier && (
                <div className="sit-farol-note">
                    <SitFarolPill tier={tier}/>
                    <span>{i18n.t(`farolNote.${tier}`, {ns: 'sit', defaultValue: ''})}</span>
                </div>
            )}

            <div className="sit-checks">
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.confidential} disabled={dis}
                                                    onChange={() => s.confidential = !s.instance.confidential}/>Confidential</label>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.nda_required} disabled={dis}
                                                    onChange={() => s.nda_required = !s.instance.nda_required}/>Under NDA</label>
                <span className="sit-check-sep">Special project:</span>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.sustainability} disabled={dis}
                                                    onChange={() => s.sustainability = !s.instance.sustainability}/>Sustainability</label>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.risk_reduction} disabled={dis}
                                                    onChange={() => s.riskReduction = !s.instance.risk_reduction}/>Risk reduction</label>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.compliance} disabled={dis}
                                                    onChange={() => s.compliance = !s.instance.compliance}/>Compliance</label>
            </div>
        </>
    );
});
